import { makeAutoObservable } from 'mobx'
import type { Academy } from 'src/dto'

import type { PermissionChecker } from '.'
import { FeaturePermission } from './FeaturePermission'
import { RoutePermission } from './RoutePermission'
import { SubscriptionPermission } from './SubscriptionPermission'

export class UserPermission implements PermissionChecker {
  private readonly _routePermission: RoutePermission
  private readonly _subscriptionPermission: SubscriptionPermission
  private _featurePermission: FeaturePermission

  private _userAuthority: res.Teacher['type']
  private _academyFeatureAuthority: res.Academy['authority']

  constructor({
    userAuthority,
    academyFeatureAuthority,
    subscribedCurriculums,
    payStatus = '무료체험',
    isSchoolflat,
    isSeoltab,
    isFranchise,
    restrictions = [],
  }: {
    userAuthority: res.Teacher['type']
    academyFeatureAuthority: res.Academy['authority']
    subscribedCurriculums: res.Academy['subscribeItems']
    payStatus: Academy['payStatus']
    isSchoolflat: boolean
    isSeoltab: boolean
    isFranchise: boolean
    restrictions?: string[]
  }) {
    makeAutoObservable(this)
    this._userAuthority = userAuthority
    this._academyFeatureAuthority = academyFeatureAuthority

    this._routePermission = new RoutePermission(
      this._userAuthority,
      payStatus,
      isSchoolflat,
      isSeoltab,
      isFranchise,
    )
    this._subscriptionPermission = new SubscriptionPermission({
      academyFeatureAuthority: this._academyFeatureAuthority,
      payStatus,
      subscribeItems: subscribedCurriculums,
      isSeoltab,
    })
    this._featurePermission = new FeaturePermission(restrictions)
  }

  get get() {
    return this._routePermission.permissionByStatus
  }

  get bySubscription() {
    return this._subscriptionPermission
  }

  /** BE ALLOW/DENY 기반 기능 권한. 라우트 게이팅 및 in-page 렌더링 제어에 사용 */
  get byFeature(): FeaturePermission {
    return this._featurePermission
  }

  isRouteAllowed(pathname: string): boolean {
    return this._featurePermission.isRouteAllowed(pathname)
  }

  updateRestrictions(restrictions: string[]): void {
    this._featurePermission = new FeaturePermission(restrictions)
  }

  get hasBasicAuth() {
    return this.checkPermission('BASIC')
  }

  get hasPremiumAuth() {
    return this.checkPermission('PREMIUM')
  }

  get hasSoloAuth() {
    return this.checkPermission('SOLO')
  }

  checkRentPermissionForSignatureWorkbook(feature?: common.CustomSignatureFeatureType) {
    if (!feature) return true

    if (feature !== 'RENT') return true

    if (this.bySubscription.workbook.signature.enableRentWorkbook) return true

    return false
  }

  checkPermission(target?: res.Academy['authority']): boolean {
    if (!target) return false
    return this._academyFeatureAuthority === target
  }
}
